/**
 * Per-iteration RUN slices of a node that executed more than once.
 *
 * WHY: a loop-body node's per-batch traces are MERGED server-side into one
 * (`executor._run_loop_node`) so it persists as a single row — which used to
 * destroy the iteration boundary, leaving the inspector showing one blob of N
 * items with no way to tell which input produced which output, which iteration
 * failed, or which was slow. The backend now records each iteration's WINDOW
 * into the merged arrays (`log_outputs.runs`, see `hub.workflow.node_runs`);
 * this module reads them so the drawer can render n8n's "Run 3 of 3 (1 item)"
 * selector.
 *
 * The windows are offsets, NOT copies — slicing is the whole contract.
 */

/** One iteration's window, mirroring the backend `run_slice` shape. */
export interface NodeRunSlice {
  /** The ITERATION index — NOT the position in the list (a skipped batch
   *  leaves gaps, and an over-cap list is sparse). Label runs with this. */
  run: number;
  in_offset: number;
  items_in: number;
  out_offset: number;
  items_out: number;
  status: string;
  duration_ms: number;
  error?: string | null;
}

/** Read the run windows off a trace's `log_outputs`, or null when the node ran
 *  once (the common case — no selector to render). Defensive: a malformed or
 *  absent bag is "no runs", never a throw, because this decorates a debug view
 *  that must keep working. */
export function runSlicesOf(logOutputs: unknown): NodeRunSlice[] | null {
  if (!logOutputs || typeof logOutputs !== "object") return null;
  const raw = (logOutputs as Record<string, unknown>).runs;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const runs = raw.filter(
    (r): r is NodeRunSlice =>
      !!r &&
      typeof r === "object" &&
      typeof (r as NodeRunSlice).run === "number" &&
      typeof (r as NodeRunSlice).out_offset === "number" &&
      typeof (r as NodeRunSlice).items_out === "number",
  );
  return runs.length > 0 ? runs : null;
}

/** How many iterations the node ACTUALLY ran — which is NOT `runs.length` when
 *  the list was capped server-side. "Run 3 of 900" must stay honest. */
export function runsTotal(logOutputs: unknown, runs: NodeRunSlice[]): number {
  const total = (logOutputs as Record<string, unknown> | null)?.runs_total;
  return typeof total === "number" && total > 0 ? total : runs.length;
}

/** True when the server capped the list — the UI should say so rather than
 *  imply the missing iterations didn't happen. */
export function runsAreCapped(logOutputs: unknown, runs: NodeRunSlice[]): boolean {
  return runsTotal(logOutputs, runs) > runs.length;
}

/**
 * Slice one iteration's items out of a merged trace array.
 *
 * Returns [] when the payload isn't a plain array — which is the TRUNCATION
 * case: an over-cap payload is replaced wholesale by a `{_truncated: true}`
 * marker server-side (never sliced), so the items are genuinely gone and the
 * caller should fall back to the per-run status/duration. It can never point
 * at the wrong rows.
 */
export function sliceRun(payload: unknown, offset: number, count: number): unknown[] {
  if (!Array.isArray(payload)) return [];
  return payload.slice(offset, offset + count);
}
